import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import "./BlogCardFixes.css";
import { useAlert } from '../context/AlertContext';

const Datex = (tarik) => {
  const date = new Date(tarik);
  const ddMmYyyy = `${date
    .getUTCDate()
    .toString()
    .padStart(2, "0")}-${(date.getUTCMonth() + 1)
    .toString()
    .padStart(2, "0")}-${date.getUTCFullYear()}`;
  return ddMmYyyy;
};

const convertBlocksToHtml = (blocks) => {
  if (!Array.isArray(blocks)) {
    if (blocks && Array.isArray(blocks.blocks)) {
      blocks = blocks.blocks;
    } else {
      console.error("Expected an array for blocks, but got:", blocks);
      return "";
    }
  }
  return blocks
    .map((block) => {
      switch (block.type) {
        case "paragraph":
          return `<p>${block.data.text}</p>`;
        case "header":
          return `<h${block.data.level}>${block.data.text}</h${block.data.level}>`;
        case "image":
          return `<img src="${block.data.file.url}" alt="${block.data.caption}" />`;
        case "list":
          const tag = block.data.style === "ordered" ? "ol" : "ul";
          return `<${tag}>${block.data.items
            .map((item) => `<li>${item}</li>`)
            .join("")}</${tag}>`;
        case "quote":
          return `<blockquote>${block.data.text}</blockquote>`;
        case "code":
          return `<pre><code>${block.data.code}</code></pre>`;
        default:
          return "";
      }
    })
    .join("");
};

const stripHtml = (html) => {
  html = html.replace(/<img[^>]*>/g, "");
  html = html.replace(/\[object Object\]/g, "").replace(/\{.*?\}/g, "");
  return html.replace(/<[^>]*>/g, "");
};

const addLineBreaks = (text, charLimit = 50) => {
  return text.replace(/(\S{50,})/g, (match) => {
    return match.replace(new RegExp(`(.{${charLimit}})`, "g"), "$1<br>");
  });
};

const truncateText = (text, wordLimit, charLimit = 50) => {
  let plainText = "";
  if (typeof text === "string") {
    plainText = text;
  } else {
    plainText = stripHtml(convertBlocksToHtml(text)).trim();
  }
  let words = plainText.split(/\s+/);
  if (words.length > wordLimit) {
    plainText = words.slice(0, wordLimit).join(" ") + "...";
  }
  return addLineBreaks(plainText, charLimit);
};

const BlogCard = (props) => {
  const showAlert = useAlert();
  const API_HOST = process.env.REACT_APP_API_URL;
  const [isHovered, setIsHovered] = useState(false);
  const authToken = sessionStorage.getItem("authToken");

  const handleDelete = async (id) => {
    // Use parent's onDelete callback if provided
    if (props.onDelete) {
      props.onDelete(id);
      return;
    }

    // Fallback to local implementation if no callback
    try {
      const response = await axios.delete(`${API_HOST}blogs/deleteBlog/${id}`, {
        headers: { "auth-token": authToken },
      });
    
      if (response.status === 200 || response.status === 204 || response.status === 201) {
        showAlert("Blog deleted successfully!", "danger");
      }
    } catch (error) {
      showAlert("Failed to delete blog. Please try again!", "danger");
      console.error("Error deleting blog:", error);
    }
  };

  const handleEdit = () => {
    if (props.onEdit) {
      props.onEdit();
    }
  };

  const visibility = props.content?.visibility || 'private';

  return (
    <div 
      className="modern-blog-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Image Container with Gradient Overlay */}
      <div className="blog-card-image-wrapper">
        <img 
          src={props.content?.homeImage || 'https://via.placeholder.com/600x400/667eea/ffffff?text=Blog+Image'} 
          alt={props.content?.title || 'Blog Image'} 
          className="blog-card-image"
        />
        <div className="blog-card-gradient-overlay"></div>
        
        {/* Status Badge */}
        <div className={`blog-status-badge ${visibility === 'public' ? 'status-public' : 'status-private'}`}>
          <span className="status-icon">{visibility === 'public' ? '🌐' : '🔒'}</span>
          <span className="status-text">{visibility === 'public' ? 'Public' : 'Private'}</span>
        </div>
      </div>

      {/* Content Container */}
      <div className="blog-card-content">
        {/* Author Info */}
        <div className="blog-card-author-section">
          <div className="author-avatar-wrapper">
            <img
              src="static/media/assets/img/blog/blog-icon_01.jpg"
              alt="Author"
              className="author-avatar"
            />
            <div className="author-avatar-ring"></div>
          </div>
          <div className="author-info">
            <p className="author-name">
              {props.content?.creator === 'creator' || !props.content?.creator ? 'Admin' : props.content?.creator}
            </p>
            <p className="blog-date">
              <svg className="date-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              {props.content?.date ? Datex(props.content.date) : 'N/A'}
            </p>
          </div>
        </div>

        {/* Title */}
        <h2 className="blog-card-title">
          {props.content?.title ? truncateText(props.content.title, 15) : 'Untitled'}
        </h2>

        {/* Excerpt */}
        <div
          className="blog-card-excerpt"
          dangerouslySetInnerHTML={{
            __html: props.content?.excerpt 
              ? addLineBreaks(props.content.excerpt, 50)
              : props.content?.content 
                ? truncateText(props.content.content, 20)
                : "No preview available",
          }}
        ></div>

        {/* Action Buttons */}
        <div className="blog-card-actions">
          <Link
            className="blog-card-view-btn"
            to={"/preview/" + `${props.content?.urlHeading || ''}`}
          >
            <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
              <circle cx="12" cy="12" r="3"></circle>
            </svg>
            Preview Blog
          </Link>
          
          <div className="blog-card-action-group">
            <button
              onClick={handleEdit}
              className="blog-action-btn edit-btn"
              title="Edit Blog"
            >
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              Edit
            </button>
            
            <button
              onClick={props.onOpenComments}
              className="blog-action-btn comments-btn"
              title="View Comments"
            >
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              Comments
            </button>
            
            <button
              onClick={() => handleDelete(props.content?._id)}
              className="blog-action-btn delete-btn"
              disabled={!props.content?._id}
              title="Delete Blog"
            >
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="blog-card-shine"></div>
    </div>
  );
};

export default BlogCard;
